import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { getAnswers } from "../services/AssessmentService";
import { Award, ChevronLeft, Trophy } from "./icons";
import { EmptyState, ProgressBar, Segmented } from "./ui";

/**
 * Scores after an assessment: the learner's own best per module, or the
 * organisation's leaders.
 *
 * This used to open with a purple semicircle and three stock medal JPGs pulled
 * from a stock-art CDN — decoration that loaded over the network, said nothing,
 * and undercut the result it was celebrating. Rank is now carried by position
 * and number, which is what a leaderboard is actually made of.
 *
 * Data handling is unchanged: one `getAnswer` call, with the module just
 * assessed hoisted to the top of both lists so the relevant row is first.
 */
/** One score line: rank (leaderboard) or award glyph (personal best). */
const Row = ({ rank, name, value, highlight }) => (
  <div className="ds-row">
    {rank ? (
      <span
        className="ds-tile"
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          fontSize: 12,
          fontWeight: 700,
          background: rank <= 3 ? "var(--award-bg)" : "var(--surface-sunken)",
          color: rank <= 3 ? "var(--award)" : "var(--ink-3)",
        }}
      >
        {rank}
      </span>
    ) : (
      <span className="ds-tile" style={{ width: 26, height: 26, borderRadius: 7 }}>
        <Award size={13} />
      </span>
    )}

    <span className="ds-grow" style={{ minWidth: 0 }}>
      <span
        className="ds-truncate"
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: highlight ? 650 : 550,
          color: highlight ? "var(--brand-700)" : "var(--ink)",
        }}
        title={name}
      >
        {name}
      </span>
    </span>

    <span style={{ width: 76, flexShrink: 0 }}>
      <ProgressBar value={value} tone={value >= 80 ? "success" : undefined} />
    </span>
    <span className="ds-bar__value">{Math.round(value)}%</span>
  </div>
);

export const AchievementModal = ({ open, show, module }) => {
  const [scope, setScope] = useState("me");
  const [myScore, setMyScore] = useState([]);
  const [teamScore, setTeamScore] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getAnswers();
        if (!response?.data) return;

        const teamScoreData = response.data.totalScore || [];
        const myScoreData = response.data.myScore || [];

        // Hoist the module just completed so its result reads first.
        const moduleIndex = teamScoreData.findIndex((d) => d._id?.name === module);
        if (moduleIndex > -1) {
          teamScoreData.unshift(teamScoreData.splice(moduleIndex, 1)[0]);
        }
        const myIndex = myScoreData.findIndex((d) => d.assessment?.name === module);
        if (myIndex > -1) {
          myScoreData.unshift(myScoreData.splice(myIndex, 1)[0]);
        }

        setMyScore(myScoreData);
        setTeamScore(teamScoreData);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [module]);

  const handleClose = () => show(false);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: 460,
          maxWidth: "92vw",
          borderRadius: "var(--r-lg)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-lg)",
        },
      }}
    >
      <div className="ds-card__head">
        <div className="ds-row-flex ds-gap-3">
          <span className="ds-tile ds-tile--brand">
            <Trophy size={18} />
          </span>
          <div>
            <h3 className="ds-h3">Scores</h3>
            <p className="ds-sub" style={{ marginTop: 2 }}>
              {scope === "me" ? "Your best per module" : "Top scorers per module"}
            </p>
          </div>
        </div>
        <Segmented
          value={scope}
          onChange={setScope}
          options={[
            { value: "me", label: "Me" },
            { value: "overall", label: "Overall" },
          ]}
        />
      </div>

      <div
        className="ds-card__body ds-scroll"
        style={{ maxHeight: 380, overflowY: "auto", paddingTop: 0 }}
      >
        {scope === "me" ? (
          myScore.length ? (
            myScore.map((data, i) => (
              <Row
                key={`${data.assessment?.name}-${i}`}
                name={data.assessment?.name || "Unknown module"}
                value={Number(data.percentage) || 0}
                highlight={data.assessment?.name === module}
              />
            ))
          ) : (
            <EmptyState icon={Trophy} text="You haven't recorded a score yet." />
          )
        ) : teamScore.length ? (
          teamScore.map((entry, i) => (
            <div key={entry._id?.name || i} style={{ paddingTop: i ? 14 : 0 }}>
              <p className="ds-eyebrow" style={{ marginBottom: 2 }}>
                {entry._id?.name || "Unknown module"}
              </p>
              {(entry.topScores || []).map((score, idx) => (
                <Row
                  key={score._id || idx}
                  rank={idx + 1}
                  name={score.user?.userName || "Unknown user"}
                  value={Number(score.percentage) || 0}
                />
              ))}
            </div>
          ))
        ) : (
          <EmptyState icon={Trophy} text="No scores recorded across the team yet." />
        )}
      </div>

      <div
        style={{
          padding: "var(--s-4) var(--s-5)",
          borderTop: "1px solid var(--border-subtle)",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          type="button"
          className="ds-btn ds-btn--secondary ds-btn--sm"
          onClick={handleClose}
        >
          <ChevronLeft size={14} />
          Close
        </button>
      </div>
    </Dialog>
  );
};

export default AchievementModal;
